import React from "react";

function BruceLee() {
  return (
    <div className="content-container resources-page">
      <h3>BRUCE LEE'S TOUGHEST FIGHT</h3>
      <quote> by Michael Dorgan (from Official Karate, July 1980)</quote>
      <p>
        Considering the skill of the opponents and the complete absence of
        referees, rules, and safety equipment, it was one hell of a fight that
        took place that day in December. It may have been the most savagely
        elegant exhibition of unarmed combat of the century. Yet, at a time when
        top fighters tend to display their skills only in huge closed-circuited
        arenas, this battle was fought in virtual secrecy behind locked doors.
        And at a time when millions of dollars can ride on the outcome of a
        championship fight, these champions of another sort competed not for
        money, but for more personal and passionate reasons. The time was late
        winter, 1964; the setting was a small kung fu school in Oakland,
        California. Poised at the center of the room, with approximately 140
        pounds packed tightly on his 5'7" frame, was the operator of the school,
        a 24-year old martial artist of Chinese ancestry but American birth who,
        within a few years, would skyrocket to international attention as a
        combination fighter/film star. A few years after that, at age 32, he
        would die under mysterious circumstances. His name, of course, was Bruce
        Lee. Also poised in the center of the room was another martial artist.
        Taller but lighter, with his 135 pounds stretched thinly over 5'10",
        this fighter was also 24 and also of Chinese descent. Born in Hong Kong
        and reared in the south of mainland China, he had only recently arrived
        in San Francisco's teeming Chinatown, just across the bay from Oakland.
        Though over the next 15 years he would become widely known in martial
        arts circles and would train some of America's top martial artists, he
        would retain a near disdain for publicity and the commercialization of
        his art, and consequently would remain unknown to the general public.
        His name: Wong Jack Man.
      </p>
      <p>
        What happened after the fighters approached the center of the room has
        become a chapter of Chinatown's "wild history," that branch of Chinese
        history usually anchored in fact but always richly embellished by
        fantasy, a history that tells much about a time and place with little
        that's reliable about any particular incident. Exactly how the fight
        proceeded and just who won are still matters of controversy, and will
        likely remain so. But from the few available firsthand accounts and
        other evidence, it is possible to piece together a reasonably reliable
        picture that reveals two overriding truths. First, considering the skill
        of the opponents and the complete absence of referees, rules, and safety
        equipment, it was one hell of a fight that took place that day in
        December. And second, Bruce Lee, who was soon to rival Mao Tse Tung as
        the world's most famous Chinese personality, was dramatically affected
        by the fight, perhaps fatally so.
      </p>
      <p>
        Due to the human desire to be known as an eye witness to a famous event,
        it is easier to obtain firsthand accounts of the fight from persons who
        were not there than from those who were. As to how many persons actually
        viewed the contest, even that is a point of dispute. Bruce Lee's wife
        Linda recalls a total of 13 persons, including herself. But the only
        person that she identifies other than her husband and his associate
        James Lee, who died of cancer shortly before her husband died, is Wong
        Jack Man. Wong, meanwhile, remembers only seven persons being present,
        including the three Lees. Of the three persons other than the Lees and
        himself, only one, a tai chi teacher named William Chen (not to be
        confused with the William Chi Cheng Chen who teaches the art in New
        York), could be located. Chen recalls about 15 persons being present but
        can identify none other than Wong and the Lees. So except for a skimpy
        reference to the fight by Bruce Lee himself in a magazine interview, we
        are left with only three firsthand accounts of the battle. They are
        accounts which vary widely.
      </p>
      <p>
        Linda Lee, in her book Bruce Lee: The Man Only I Knew, initially
        dismisses the fight as follows: "The two came out, bowed formally and
        then began to fight. Wong adopted a classic stance whereas Bruce, who at
        the time was still using his Wing Chun style, produced a series of
        straight punches. "Within a minute, Wong's men were trying to stop the
        fight as Bruce began to warm to his task. James Lee warned them to let
        the fight continue. A minute later, with Bruce continuing the attack in
        earnest, Wong began to backpedal as fast as he could. For an instant,
        indeed, the scrap threatened to degenerate into a farce as Wong actually
        turned and ran. But Bruce pounced on him like a springing leopard and
        brought him to the floor where he began pounding him into a state of
        demoralization.
      </p>
      <p>
        "Is that enough?" shouted Bruce. "That's enough!" pleaded Wong in
        desperation. So the entire matter was just another quick triumph for the
        man who frequently boasted he could whip any man in the world. Or was
        it? Later in her book, Linda Lee hints that the fight may have amounted
        to more than the brief moment of violent diversion she had earlier
        described. "Bruce's whole life was an evolving process - and this was
        never seen to greater effect than in his work with the martial arts,"
        she begins. "The clash with Wong Jack Man metamorphosed his own personal
        expression of kung fu. Until this battle, he had largely been content to
        improvise and expand on his original Wing Chun style, but then he
        suddenly realized that although he had won comparatively easily, his
        performance had been neither crisp of efficient. The fight, he realized,
        ought to have ended within a few seconds of him striking the first blows
        - instead of which it had dragged on for three minutes. In addition, at
        the end, Bruce had felt unusually winded which proved to him he was far
        from perfect condition. So he began to dissect the fight, analyzing
        where he had gone wrong and seeking to find ways where he could have
        improved his performance. It did not take him long to realize that the
        basis of his fighting art, the Wing Chun style, was insufficient. It
        laid too much stress on hand techniques, had very few kicking techniques
        and was, essentially, partial."
      </p>
      <p>
        Still later in the book, Linda Lee adds: "The Wong Jack Man fight also
        caused Bruce to intensify his training methods. From that date, he began
        to seek out more and more sophisticated and exhaustive training methods.
        I shall try to explain these in greater detail later, but in general the
        new forms of training meant that Bruce was always doing something,
        always training some part of his body or keeping it in condition."
      </p>
      <p>
        Whether Bruce Lee's intensified training was to his benefit or to his
        destruction is a matter to be discussed later. For now, merely let it be
        observed that the allegedly insignificant "scrap" described early by
        Linda Lee has now been identified by her as cause for her husband to
        intensify his training and serves as the pivotal reason for his
        abandoning the fighting style he had practiced religiously for more than
        10 years.
      </p>
      <p>
        That the fight with Wong was the reason Lee quit, and then later
        repudiated the Wing Chun style, was confirmed by Lee himself in an
        interview with Black Belt. "I'd gotten into a fight in San Francisco (a
        reference, no doubt, to the Bay Area rather than the city) with a
        Kung-Fu cat, and after a brief encounter the son-of-a-bitch started to
        run. I chased him and, like a fool, kept punching him behind his head
        and back. Soon my fists began to swell from hitting his hard head. Right
        then I realized Wing Chun was not too practical and began to alter my
        way of fighting." For those who have difficulty believing that a quick
        if clumsy victory over a worthy opponent was sufficient reason for Lee
        to abandon a fighting style that had seen him through dozens of vicious
        street fights as a youth in Hong Kong, where his family had moved
        shortly after his birth in San Francisco, a more substantial reason for
        Lee to change styles can be found in the account of the fight given by
        Wong Jack Man.
      </p>
      <p>
        According to Wong, the battle began with him bowing and offering his
        hand to Lee in the traditional manner of opening a match. Lee, he say,
        responded by pretending to extend a friendly hand only to suddenly
        transform the hand into a four-pronged spear aimed at Wong's eyes.
      </p>
      <p>
        "That opening move," says Wong, "set the tone for Lee's fight." Wing
        Chun has but three sets, the solo exercises which contain the full body
        of technique of any style, and one of those sets is devoted to deadly
        jabbing and gouging attacks directed primarily at the eyes and throat.
        "It was those techniques," say Wong, "which Lee used most."
      </p>
      <p>
        There were flurries of straight punches and repeated kicks at his groin,
        adds Wong, but mostly, relentlessly, there were those darting deadly
        finger tips trying to poke out his eyes or puncture his throat. And what
        he say he anticipated as serious but sportsmanly comparison of skill
        suddenly became an exercise in defending his life.
      </p>
      <p>
        Wong says that before the fight began Lee remarked, in reference to a
        mutual acquaintance who had helped instigate the match, "You've been
        killed by your friend." Shortly after the bout commenced, he adds, he
        realized Lee's words had been said in earnest.
      </p>
      <p>
        "He really wanted to kill me," says Wong. In contrast to Lee's three
        Wing Chun sets, Wong, as the grand master of the Northern Shaolin style,
        knew dozens. But most of what he used against Lee, says Wong, was
        defensive. Wong says he parried Lee's kicks with his legs while using
        his hand and arms to protect his head and torso, only occasionally
        delivering a stinging blow to Lee's head or body. He fought defensively,
        explains Wong, in part because of Lee's relentless aggressive strategy,
        and in part because he feared the consequences of responding in kind to
        Lee's attempt to kill him. In pre-Revolutionary China, fights to the
        finish were often allowed by law, but Wong knew that in modern-day
        America, a crippling or killing blow, while winning a victory, might
        also win him a jail sentence.
      </p>
      <p>
        That, says Wong, is why he failed to deliver a devastating right-hand
        blow on any of the three occasions he had Lee's head locked under his
        left arm. Instead, he says, he released his opponent each time, only to
        have an even more enraged Bruce Lee press on with his furious attack.
        "He would never say he lost until you killed him," says Wong. And
        despite his concern with the legal consequences, Wong says that killing
        Lee is something he began to consider. "I remember thinking, 'If he
        injures me, if he really hurts me, I'll have to kill him."
      </p>
      <p>
        But according to Wong, before that need arose, the fight had ended, due
        more to what Linda Lee described as Lee's "unusually winded" condition
        than to a decisive blow by either opponent. "It had lasted," says Wong,
        "at least 20 minutes, maybe 25."
      </p>
      <p>
        Though William Chen's recollections of the fight are more vague than the
        other two accounts, they are more in alignment with Wong's than Lee's.
        On the question of duration, for example, Chen, like Wong, remembers the
        fight continuing for "20 or 25 minutes." Also, he cannot recall either
        man being knocked down. "Certainly," he says, "Wong was not brought to
        the floor and pounded into a 'state of demoralization.'"
      </p>
      <p>
        Regarding Wong's claim that three times he had Lee's head locked under
        his arm, Chen says he can neither confirm or deny it. He remembers the
        fighters joining on several occasions, but he could not see very clearly
        what was happening at those moments.
      </p>
      <p>
        Chen describes the outcome of the battle as "a tie." He adds, however,
        that whereas an enraged Bruce Lee had charged Wong "like a mad bull,"
        obviously intent upon doing him serious injury. Wong had displayed
        extraordinary restraint by never employing what were perhaps his most
        dangerous weapons - his devastating kicks.
      </p>
      <p>
        A principal difference between northern and southern Chinese fighting
        styles is that the northern styles give much more emphasis to kicking,
        and Northern Shaolin had armed Wong with kicks of blinding speeds and
        crushing power. But before the fight, recalls Chen, "Sifu Wong said he
        would not use his kicks; he thought they were too dangerous." And
        despite the dangerous developments that followed that pledge, Chen adds
        that Wong "kept his word." Though Chen's recollections exhaust the
        firsthand accounts, there are further fragments of evidence to indicate
        how the fight ended.
      </p>
      <p>
        Ming Lum, who was then a San Francisco martial arts promoter, says he
        did not attend the fight because he was a friend of both Lee and Wong,
        and feared that a battle between them would end in serious injury, maybe
        even death. "Who," he asks, "would have stopped them?" But Lum did see
        Wong the very next day at the Jackson Cafe, where the young grand master
        earned his living as a waiter (he had, in fact, worked a full shift at
        the busy Chinatown restaurant the previous day before fighting Lee). And
        Lum says the only evidence he saw of the fight was a scratch above one
        eye, a scratch Wong says was inflicted when Lee went for his eyes as he
        extended his arm for the opening handshake.
      </p>
      <p>
        "Some people say Bruce Lee beat up Jack Man bad," note Lum. "But if he
        had, the man would not have been to work the next day." By Lum's
        assessment, the fact that neither man suffered serious injury in a
        no-holds-barred battle indicates that both were "very, very good." Both
        men were no doubt, very, very, good. But Wong, after the fight, felt
        compelled to assert, boldly and publicly, that he was the better of the
        two. He did so, he says, only because Lee violated their agreement to
        not discuss the fight.
      </p>
      <p>
        According to Wong, immediately following the match Lee had asked that
        neither man discuss it. Discussion would lead to more argument over who
        had won, a matter which could never be resolved as there had been no
        judges. Wong said he agreed.
      </p>
      <p>
        But within a couple of weeks, he says, Lee violated the agreement by
        claiming in an interview that he had defeated an unnamed challenger.
        Though Lee had not identified Wong as the loser, Wong says it was
        obvious to all of Chinatown that Lee was speaking of Wong. It had
        already become common knowledge within the Chinese community that the
        two had fought. In response to Lee's interview, Wong wrote a detailed
        description of the fight which concluded with an open invitation to Lee
        to meet him for a public bout if Lee was not satisfied with Wong's
        account. Wong's version of the fight, along with the challenge, was run
        as the top story on the front page of San Francisco's Chinese language
        Chinese Pacific Weekly. But Bruce Lee, despite his reputation for
        responding with fists of fury to the slightest provocation, remained
        silent.
      </p>
      <p>
        Now death has rendered the man forever silent. And the question of
        whether Wong presented Lee, who is considered by many to have been the
        world's top martial artist, with the only defeat of his adult life will
        remain, among those concerned about such matters, forever a
        controversial one. Even those Bruce Lee fans who accepts the evidence as
        supportive of Wong's account of the fight may argue that the outcome
        would have been different had the two battled a few years after Lee had
        developed his own style, Jeet Kune Do. But while it is true that Jeet
        Kune Du provided lee with a wider range of weapons, particularly kicks,
        it is also true that Wong continued to grow as a martial artist after
        the fight. Only after that battle, says Wong, did he develop tremendous
        chi powers from the practice of Tai Chi, Hsing I, and Pakua.
      </p>
      <p>
        Martial art styles can be divided roughly into two categories: external
        and internal. External styles, which are also called "hard" styles and
        which include such American favorites as Japanese karate and Korean
        taekwondo, rely primarily upon muscular strength, while internal or
        "soft" styles, such as Japanese Aikido and the three above-mentioned
        Chinese styles, cultivate a more mysterious energy called chi.
      </p>
      <p>
        Although everybody has chi, few people have much of it, and fewer still
        know how to express it. But according to the Chinese, this precious
        elixir can be cultivated and controlled through the exercises of the
        internal martial arts styles.
      </p>
      <p>
        Specifically, they say chi can be brewed in the tan tien, a spot about
        an inch below the navel. Once the tan tien is filled, the chi supposedly
        spills out into other parts of the body, where it is stored in the
        marrow of the bones. It is said that as a martial artist develops chi
        energy, his bones become hard, his sinews tough, is muscles supple and
        relaxed, which allow the chi to circulate freely through the body.
      </p>
      <p>
        Chi usually takes much longer to develop than muscular strength, but it
        is considered a much more formidable energy. In normal times it is said
        to serve as a source of extraordinary vitality and as a guardian against
        my diseases. And in battle, it is said to provide a person with awesome
        power and near invulnerability.
      </p>
      <p>
        Though Wong had been trained in the internal styles while still in
        China, up until the time he fought Lee he had concentrated mainly on the
        refinement of his elegantly athletic Northern Shaolin, which, like Lee's
        Wing Chun, is an external style. Following the battle with Lee, Wong
        would train in the internal styles until he had developed such chi power
        that he can, according to Peter Ralston, a former student of Wong and
        the first non-Asian to win the Chinese Martial Arts World Championships
        in Taiwan, take a punch to any part of his body without injury or even
        discomfort. As for Wong's offensive capabilities, they have apparently
        never been tested.
      </p>
      <p>
        Regarding the question of how much Lee grew as a martial artist after
        the fight, Wong is convinced that the benefits to Lee from his homemade
        style were more than offset by the damage it did him. Wong even goes so
        far as to speculate that Jeet Kune Do may have caused Lee's death.
      </p>
      <p>
        Most martial arts masters agree that just as serious training in a
        proper method can greatly improve one's health, strenuous and prolonged
        training in an improper method can destroy health. Of the health damage
        is attributed to improper breathing practices, and often the damage is
        to the brain. Special use of the breath is acknowledged by every martial
        arts style as a key element to developing power, though different styles
        have different breathing methods. Proper methods can be simply
        categorized as those which develop power while building health, and
        improper methods as those which either fail to build power or build it
        but at the expense of one's health. Though many of the ways in which
        breathing methods affect health remain mysterious, the methods
        themselves - at least the proper methods - have been empirically refined
        over many generations. Wong's Northern Shaolin, for example, can be
        traced back to the great Shaolin Temple of more than a thousand years
        ago, which is considered the source of Chinese martial arts. While the
        Wing Chun practiced by Lee until his fight with Wong also had a long
        period of development and refinement, the style he put together after
        the fight was a chop suey of many and varied ingredients.
      </p>
      <p>
        That Jeet Kune Do lacked the cohesion and harmony of a style in the
        traditional sense was something acknowledged by Lee himself, who
        preferred to call it a "sophisticated form of street fighting" rather
        than a style. After abandoning Wing Chin, Lee developed a disdain for
        all traditional styles, which he considered restrictive and ineffective.
        He even went so far as to place outside his school a mock tombstone that
        read: "In memory of a once fluid man crammed and distorted by the
        classical mess." It is grimly ironic that it would be Lee would be in
        need of a tombstone long before the man, trained by and loyal to the
        "classical mess," who was almost certainly his most formidable opponent.
      </p>
      <p>
        It cannot be proven, of course, that Lee's fatal edema of the brain was
        caused by Jeet Kune Do, just as it could not be proven his death was
        brought on by any of the other rumored causes ranging from illicit drugs
        to excessive sex to blows on the head. Wong thinks, to serve as a
        caution to those who believe they can, by themselves, develop the
        knowledge it has taken others many generations of cumulative effort to
        acquire.
      </p>
      <p>
        Perhaps it is because he gives so much credit to those who came before
        him that Wong's voice is absent of boast when he says his art was
        superior to Lee's. But while to him that is a matter of simple fact,
        Wong, aware that legends are larger than men, is not optimistic about
        ever being accepted as the winner of the fight. He says, however, that
        what people think regarding the outcome of the fight is less important
        to him than what they think provoked the battle in the first place.
      </p>
      <p>
        In Linda Lee's account, which has been repeated in a number of Bruce Lee
        biographies, Wong is portrayed not only as a loser but also as a
        villian. According to Ms. Lee, Wong provoked the fight in an attempt to
        force her husband to stop teaching Kung Fu to Caucasians.
      </p>
      <p>
        After sketching a brief history of Chinese martial arts up to the Boxer
        Rebellion, she writes: "Since then - and the attitude is understandable
        - Chinese, particularly in America, have been reluctant to disclose
        these secrets to Caucasians. It became an unwritten law that the art
        should be taught only to Chinese. Bruce considered such thinking
        completely outmoded and when it was argued that white men, if taught the
        secrets, would use the art to injure the Chinese, he pointed out that if
        a white man really wanted to injure a Chinese, there were plenty of
        other ways he could do it. "However, Bruce soon found that at first his
        views were not shared by members of the Chinese community in San
        Francisco, particularly those in martial arts' circles. Several months
        after he and James Lee had begun teaching, a kung fu expert called Wong
        Jack Man turned up at Bruce's kwoon (school) on Broadway. Wong had just
        recently arrived in San Francisco's Chinatown from Hong Kong and was
        seeking to establish himself at the time, all his pupils being strictly
        pure Chinese. Three other Chinese accompanied Wong Jack Man who handed
        Bruce an ornate scroll which appears to have been an ultimatum from the
        San Francisco martial arts community. Presumably, if Bruce lost the
        challenge, he was either to close down his Institute or stop teaching
        Caucasians."
      </p>
      <p>
        So by Linda Lee's account, her husband had suddenly found himself in a
        position no less heroic than of having to defend, possibly to the death,
        the right to teach Caucasians the ancient Chinese fighting secrets. It
        is a notion that Wong finds ridiculous.
      </p>
      <p>
        The reason he showed up at Lee's school that day, says Wong, is because
        a mutual acquaintance had hand-delivered a note from Lee inviting him to
        fight. The note was sent, say Wong, after he had requested a public bout
        with Lee after Lee had boasted during a demonstration at a Chinatown
        theater that he could beat any martial artist in San Francisco and had
        issued an open challenge to fight anyone who thought he could prove him
        wrong. As for those in attendance at the fight, Wong says he only knew
        of few of them, and those barely. Certainly, he says, no group had come
        as formal representative of the San Francisco martial arts community.
        Wong attributes both Lee's initial challenge and his response to the
        same emotion, to arrogance. "If I had it to do over," he says, " I
        wouldn't." But while admitting to youthful arrogance, Wong strongly
        contests Linda Lee's allegation that he was guilty of trying to stop
        Bruce Lee from teaching Caucasians.
      </p>
      <p>
        It is true, say Wong, that most - but not all - of his students during
        his first years were teaching were Chinese. But that was true, he adds,
        only because few Americans outside of Chinese communities had even heard
        of kung fu. Americans who then knew anything at all of the martial arts
        most likely knew of Japanese judo or karate. They would not hear of kung
        fu until several years later, when it would be made famous by the
        dazzling choreography's of Bruce Lee.
      </p>
      <p>
        Far from attempting to keep kung fu secret and exclusive, Wong observes
        that his was the first school in San Francisco's Chinatown to operate
        with open doors. That the other kung fu schools then in existence
        conducted classes behind locked doors was due more to the instructor's
        fears of being challenged, say Wong, than to a refusal to teach
        Caucasians. Once Caucasians became interested in kung fu, it would be
        Wong who would train some of the best of them, including Ralston and
        several other leading West Coast instructors. And all of these students
        of Wong who currently teaches at San Francisco's Fort Mason Center would
        be taught for a monthly fee amounting to a fraction of the hourly rate
        (in some cases $500) charged by the man who allegedly fought for the
        right to teach them.
      </p>
    </div>
  );
}

export default BruceLee;
